import { useCallback, useMemo } from 'react';
import { useOkapiKy } from '@folio/stripes/core';
import { useQuery } from 'react-query';
/**
 * Resolves organization (library) UUIDs to display names for list views (CZNA-597).
 *
 * <p>Organizations live in mod-czna-users, so a proposal carries only
 * `organizationId` — mod-czna-authorities has no name to give. Routing the lookup
 * through the backend was considered and rejected: mod-czna-authorities would have
 * to call mod-czna-users over HTTP anyway (CznaUsersClient), forwarding the SAME
 * user token into the SAME permission gate, once per list page. One cached
 * collection read from the browser costs strictly less and adds no coupling.
 *
 * <p>The endpoint caps `limit` at 500 (OrganizationController, `@Max(500)`). Past
 * that the surplus organizations stay unresolved and {@link getOrganizationName}
 * returns null, which the caller renders as the raw UUID — visibly wrong rather
 * than silently blank.
 */
var ORGANIZATIONS_PATH = 'czna-users/organizations';
var ORGANIZATIONS_LIMIT = 500;
var STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes — libraries change far more slowly
var useOrganizationNames = function () {
    var ky = useOkapiKy();
    var _a = useQuery(['czna-users-organizations', ORGANIZATIONS_LIMIT], function () {
        var searchParams = new URLSearchParams({
            query: 'cql.allRecords=1',
            limit: String(ORGANIZATIONS_LIMIT),
        });
        return ky.get("".concat(ORGANIZATIONS_PATH, "?").concat(searchParams)).json();
    }, {
        staleTime: STALE_TIME_MS,
        // Every logged-in user holds czna-users.organizations.collection.get since
        // CZNA-776 -- until then an ordinary cataloguer got a 403 here and the whole
        // column rendered UUIDs. The read can still fail (a user whose capabilities
        // were never resynced, a module restart), and when it does it must degrade to
        // "no names" rather than three retries per list render.
        retry: false,
    }), data = _a.data, isLoading = _a.isLoading;
    var namesById = useMemo(function () {
        var _a;
        var map = new Map();
        ((_a = data === null || data === void 0 ? void 0 : data.items) !== null && _a !== void 0 ? _a : []).forEach(function (organization) {
            if ((organization === null || organization === void 0 ? void 0 : organization.id) && (organization === null || organization === void 0 ? void 0 : organization.name)) {
                map.set(organization.id, organization.name);
            }
        });
        return map;
    }, [data]);
    // CZNA-1476 — the record detail names an open proposal's library by its sigla.
    var siglasById = useMemo(function () {
        var _a;
        var map = new Map();
        ((_a = data === null || data === void 0 ? void 0 : data.items) !== null && _a !== void 0 ? _a : []).forEach(function (organization) {
            if ((organization === null || organization === void 0 ? void 0 : organization.id) && (organization === null || organization === void 0 ? void 0 : organization.sigla)) {
                map.set(organization.id, organization.sigla);
            }
        });
        return map;
    }, [data]);
    /** The organization's name, or null when the id is unknown, missing or unresolvable. */
    var getOrganizationName = useCallback(function (id) { var _a; return (id ? (_a = namesById.get(id)) !== null && _a !== void 0 ? _a : null : null); }, [namesById]);
    /** The organization's sigla, or null when the id is unknown or the library has none. */
    var getOrganizationSigla = useCallback(function (id) { var _a; return (id ? (_a = siglasById.get(id)) !== null && _a !== void 0 ? _a : null : null); }, [siglasById]);
    return { getOrganizationName: getOrganizationName, getOrganizationSigla: getOrganizationSigla, isLoading: isLoading };
};
export default useOrganizationNames;
